/**
 * Home "Your balances" (Oct 2026, Falco's feedback): remaining vacation (and
 * the follow-on pot once it is open) for Absence users, plus the overtime
 * balance for people who track their own time. Same maths as the Absence
 * dashboard and the Time Tracking page (computeAbsenceOverview /
 * computeStatusMinutes), so the numbers never disagree.
 */
import { Link } from 'react-router-dom';
import {
  minutesToHhMm,
  overtimeCompensationMinutes,
  resolveLeaveTypes,
  type AppConfig,
  type User as UserDoc, holidaysFor,} from '@daaily/shared';
import { card } from '../design/ui';
import { useMyAbsences } from '../absence/hooks/useAbsence';
import { computeAbsenceOverview } from '../absence/AbsenceOverview';
import { computeStatusMinutes, todayStr, useMyTimeEntries } from '../timeTracking/lib';

export function BalancesCard({ user, config }: { user: UserDoc; config: AppConfig }) {
  const absence = !!user.tools?.absence?.enabled;
  const tracks = !!user.tools?.timeTracking?.enabled && user.timeTracksOwn !== false && !!user.weeklyWorkingHours && !!user.timeTrackingStartDate;
  const email = user.email.toLowerCase();
  const requests = useMyAbsences(absence || tracks ? email : null);
  const entries = useMyTimeEntries(tracks ? email : null);
  if (!absence && !tracks) return null;

  const overview = absence ? computeAbsenceOverview(user, config, requests) : null;
  const vacation = overview?.summaryFor('Vacation') ?? null;
  const dependent = overview?.waterfall?.dependentOpen && overview.dependentPolicy ? overview.waterfall.dependent : null;

  let overtimeMin: number | null = null;
  if (tracks && requests !== null) {
    const holidays = holidaysFor(config, user.holidayCalendarId);
    const leaveTypes = resolveLeaveTypes(config);
    overtimeMin = computeStatusMinutes({
      startDate: user.timeTrackingStartDate!,
      openingBalanceHours: user.timeTrackingOpeningBalanceHours,
      today: todayStr(),
      entries,
      weeklyHours: user.weeklyWorkingHours!,
      holidays,
      absences: requests.filter((a) => a.status === 'approved'),
      overtimeCompMinutes: overtimeCompensationMinutes(requests, leaveTypes, user, holidays),
      workTimeUser: user,
      nowMs: Date.now(),
    });
  }

  const loading = requests === null;
  return (
    <aside aria-labelledby="balances-h" className={`${card} p-4`}>
      <h2 id="balances-h" className="text-base font-semibold text-fg">Your balances</h2>
      <dl className="mt-3 grid grid-cols-2 gap-4">
        {absence && vacation && (
          <Link to="/absence" className="block rounded-md -m-1 p-1 hover:bg-surface-2 transition-colors duration-fast">
            <dt className="text-2xs font-semibold uppercase text-fg-faint">Vacation left</dt>
            <dd className="mt-1 text-2xl font-semibold tabular-nums text-fg">
              {loading ? '…' : vacation.remaining == null ? '—' : vacation.remaining}
              {!loading && vacation.total != null && <span className="text-sm font-normal text-fg-faint"> / {vacation.total} days</span>}
            </dd>
            {!loading && vacation.plannedRequested > 0 && (
              <p className="text-xs text-fg-faint">{vacation.plannedRequested} requested, not yet approved</p>
            )}
            {!loading && dependent && overview?.dependentPolicy && (
              <p className="text-xs text-fg-faint">{overview.dependentPolicy.name}: {dependent.remaining} left</p>
            )}
          </Link>
        )}
        {tracks && (
          <Link to="/time" className="block rounded-md -m-1 p-1 hover:bg-surface-2 transition-colors duration-fast">
            <dt className="text-2xs font-semibold uppercase text-fg-faint">Overtime balance</dt>
            <dd className={`mt-1 text-2xl font-semibold tabular-nums ${overtimeMin == null ? 'text-fg' : overtimeMin >= 0 ? 'text-success' : 'text-danger'}`}>
              {overtimeMin == null ? '…' : `${overtimeMin >= 0 ? '+' : '-'}${minutesToHhMm(Math.abs(overtimeMin))}`}
            </dd>
            <p className="text-xs text-fg-faint">Year to date</p>
          </Link>
        )}
      </dl>
    </aside>
  );
}
